import { Form, FormElement, FormError, FormGroup, FormSubmission, NextBehavior, ReducerType } from '@uplift/types';
import React, { useContext } from 'react';


type InitializedRenderFormContextData = {
  initialized: true;
  element?: FormElement;
  form: Form;
  groups: FormGroup[];
  loading: boolean;
  totalElements: number;
  submissionId?: string;
  nextBehavior: NextBehavior;
  error?: FormError;
  elements: FormElement[];
  submission?: FormSubmission;
  reviewPageElement?: FormElement;
  shouldRefetch?: boolean;
  answerVal?: string | string[];
};

type InitialRenderFormContextData = Partial<Omit<InitializedRenderFormContextData, 'initialized'> & {
  initialized: false;
}>;

export type RenderFormContextData = InitialRenderFormContextData | InitializedRenderFormContextData;

export type RenderFormContextState = {
  state: RenderFormContextData;
  dispatch: React.Dispatch<Action>;
};

type InitAction = {
  type: 'init';
  payload: {
    form: Form;
    element: FormElement;
    totalElements: number;
    submissionId: string;
  };
};

type InitPreviewAction = {
  type: 'initPreview';
  payload: {
    form: Form;
    elements: FormElement[];
  };
};

type SetErrorAction = {
  type: 'setError';
  payload: FormError;
};

type GetElementAction = {
  type: 'getElement';
};

type SetElementAction = {
  type: 'setElement';
  payload: {
    element: FormElement;
    shouldRefetch: boolean;
    answerVal?: string | string[];
  };
};

type GetReviewPageAction = {
  type: 'getReviewPage';
};

type SetReviewPageAction = {
  type: 'setReviewPage';
  payload: {
    elements: FormElement[];
    submission: FormSubmission;
  };
};

type SetEditElementAction = {
  type: 'setEditElement';
  payload: {
    element: FormElement;
    answerVal: string | string[];
  };
};

type CancelEditAction = {
  type: 'cancelEdit';
};

export type Action =
  | InitAction
  | InitPreviewAction
  | SetErrorAction
  | GetElementAction
  | SetElementAction
  | GetReviewPageAction
  | SetReviewPageAction
  | SetEditElementAction
  | CancelEditAction;

const handlers = {
  init: (state: InitialRenderFormContextData, action: InitAction): RenderFormContextData => ({
    ...state,
    initialized: true,
    loading: false,
    elements: [],
    form: action.payload.form,
    element: action.payload.element,
    groups: action.payload.form.formGroups,
    totalElements: action.payload.totalElements,
    submissionId: action.payload.submissionId,
    nextBehavior: NextBehavior.Linear,
  }),
  initPreview: (state: InitialRenderFormContextData, action: InitPreviewAction): RenderFormContextData => ({
    ...state,
    initialized: true,
    form: action.payload.form,
    elements: action.payload.elements,
    groups: action.payload.form.formGroups,
    loading: false,
    totalElements: action.payload.elements.length,
    nextBehavior: NextBehavior.Linear,
  }),
  setError: (state: InitializedRenderFormContextData, action: SetErrorAction): RenderFormContextData => ({
    ...state,
    error: action.payload
  }),
  getElement: (state: InitializedRenderFormContextData, _action: GetElementAction): RenderFormContextData => ({
    ...state,
    loading: true
  }),
  setElement: (state: InitializedRenderFormContextData, action: SetElementAction): RenderFormContextData => ({
    ...state,
    loading: false,
    element: action.payload.element,
    shouldRefetch: action.payload.shouldRefetch,
    answerVal: action.payload.answerVal,
  }),
  getReviewPage: (state: InitializedRenderFormContextData, _action: GetReviewPageAction): RenderFormContextData => ({
    ...state,
    loading: true
  }),
  setReviewPage: (state: InitializedRenderFormContextData, action: SetReviewPageAction): RenderFormContextData => ({
    ...state,
    loading: false,
    elements: action.payload.elements,
    submission: action.payload.submission,
    reviewPageElement: state.element,
    nextBehavior: NextBehavior.SkipToLast,
    shouldRefetch: false,
  }),
  setEditElement: (state: InitializedRenderFormContextData, action: SetEditElementAction): RenderFormContextData => ({
    ...state,
    loading: false,
    element: action.payload.element,
    answerVal: action.payload.answerVal,
  }),
  cancelEdit: (state: InitializedRenderFormContextData, _action: CancelEditAction): RenderFormContextData => ({
    ...state,
    element: state.reviewPageElement
  }),
};

export const reducer: ReducerType<RenderFormContextData, Action> = (state, action) => {
  switch (action.type) {
    case 'init':
      return state.initialized === false ? handlers.init(state, action) : state;
    case 'initPreview':
      return state.initialized === false ? handlers.initPreview(state, action) : state;
    case 'setError':
      return state.initialized === true ? handlers.setError(state, action) : state;
    case 'getElement':
      return state.initialized === true ? handlers.getElement(state, action) : state;
    case 'setElement':
      return state.initialized === true ? handlers.setElement(state, action) : state;
    case 'getReviewPage':
      return state.initialized === true ? handlers.getReviewPage(state, action) : state;
    case 'setReviewPage':
      return state.initialized === true ? handlers.setReviewPage(state, action) : state;
    case 'setEditElement':
      return state.initialized === true ? handlers.setEditElement(state, action) : state;
    case 'cancelEdit':
      return state.initialized === true ? handlers.cancelEdit(state, action) : state;
  }

  return state;
};

export const RenderFormContext = React.createContext<RenderFormContextState | null>(null);

export const useInitializedRenderFormContext = () => {
  const context = useContext(RenderFormContext);

  if (!context) {
    throw new Error('useRenderFormContext must be used within a RenderFormContext.Provider');
  }

  const { state, dispatch } = context;

  if (!state.initialized) {
    throw new Error('RenderFormContext was not initialized yet');
  }

  return { state, dispatch };
};
